import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HumanTaskDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, Panel, type Tone } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { openJarvis } from "@/jarvis/jarvis-store";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "../decisions/project-filter";
import { ConfirmModal, InboxEmptyRow, InboxShell, useInboxToast } from "../inbox/InboxShell";
import { useIsMobile } from "@/lib/useIsMobile";
import { MobileHumanTasks } from "@/mobile/MobileHumanTasks";

const urgencyTone: Record<string, Tone> = {
  URGENT: "red",
  HIGH: "amber",
  NORMAL: "cyan",
  LOW: "dim",
};

/**
 * Úkoly pro člověka.
 *
 * Vědomě tu NEJSOU tlačítka Schválit doporučení / Zamítnout: „zkontroluj vzhled
 * a diakritiku na třech vytištěných páskách" nejde schválit, jde jen zkontrolovat
 * a napsat, co na nich je. Hlavní akce obrazovky je proto textové pole — nález,
 * který jde workerovi jako vstup do práce.
 */
export function HumanTasksPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileHumanTasks />;
  return <DesktopHumanTasks />;
}

function DesktopHumanTasks() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { toast, showToast } = useInboxToast();
  const { project: rawProjectFilter = ALL_PROJECTS } = useSearch({ from: "/shell/human-tasks" });

  const query = useQuery({
    queryKey: ["human-tasks"],
    queryFn: () => api.humanTasks(),
  });

  const undoMutation = useMutation({
    mutationFn: (id: string) => api.reopenHumanTask(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
      showToast(cs.humanTasks.undoneToast);
    },
    onError: (err) => showToast(err instanceof Error ? err.message : cs.common.error),
  });

  const allItems = query.data?.items ?? [];
  const projectFilter = resolveProjectFilter(projectGroups(allItems), rawProjectFilter);
  const items = filterByProject(allItems, projectFilter);

  return (
    <InboxShell
      testId="human-tasks"
      title={cs.humanTasks.title}
      kicker="ÚKOLY"
      lead={cs.humanTasks.lead(items.length)}
      right={
        <ProjectFilterTabs
          items={allItems}
          value={projectFilter}
          onChange={(key) => {
            void navigate({
              to: "/human-tasks",
              search: key === ALL_PROJECTS ? {} : { project: key },
            });
            setSelectedId(null);
          }}
          testIdPrefix="human-tasks"
        />
      }
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      emptyList={<InboxEmptyRow title={cs.humanTasks.emptyTitle} note={cs.humanTasks.emptyNote} />}
      emptyDetail={cs.humanTasks.emptyDetail}
      emptyDetailNote={cs.humanTasks.emptyDetailNote}
      toast={toast}
      onUndo={(id) => undoMutation.mutate(id)}
      undoPending={undoMutation.isPending}
      renderRow={(t) => (
        <>
          <div className="af-mono flex justify-between text-[12px] tracking-[0.1em]">
            <span className="font-bold" style={{ color: `var(--${urgencyTone[t.urgency] === "red" ? "critical" : urgencyTone[t.urgency] === "amber" ? "warning" : "accent"})` }}>
              {t.urgency}
              {t.blocksProject ? (
                <span style={{ color: "var(--critical)" }}> · {cs.humanTasks.blocksProjectTag}</span>
              ) : null}
            </span>
            <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(t.createdAt)}</span>
          </div>
          <div className="af-card-title mb-[3px] mt-[5px] leading-snug">{t.title}</div>
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {t.projectName ?? "Factory"}
            {t.blockedTasks.length ? ` · blokuje ${t.blockedTasks.length} tasků` : ""}
          </div>
        </>
      )}
      renderDetail={(t) => (
        <HumanTaskDetail
          key={t.id}
          item={t}
          onClosed={(msg, undoId) => {
            void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
            void queryClient.invalidateQueries({ queryKey: ["projects"] });
            if (t.projectId) {
              void queryClient.invalidateQueries({ queryKey: ["section", t.projectId] });
            }
            showToast(msg, undoId);
            setSelectedId(null);
          }}
        />
      )}
    />
  );
}

function HumanTaskDetail({
  item,
  onClosed,
}: {
  item: HumanTaskDTO;
  onClosed: (msg: string, undoId?: string) => void;
}) {
  const { session } = useAuth();
  const [finding, setFinding] = useState("");
  const [dismissReason, setDismissReason] = useState("");
  const [dismissOpen, setDismissOpen] = useState(false);
  const [confirming, setConfirming] = useState<"DONE" | "DISMISSED" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const completeMutation = useMutation({
    mutationFn: () => api.completeHumanTask(item.id, { finding: finding.trim() }),
    onSuccess: (data) => onClosed(cs.humanTasks.doneToast(data.unblockedCount), item.id),
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const dismissMutation = useMutation({
    mutationFn: () => api.dismissHumanTask(item.id, { reason: dismissReason.trim() }),
    onSuccess: () => onClosed(cs.humanTasks.dismissedToast, item.id),
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const canClose = session?.user.role !== "CLIENT";
  const urgTone = urgencyTone[item.urgency] ?? "cyan";

  return (
    <div className="max-w-[860px]" data-testid="human-tasks-detail">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="af-mono flex flex-wrap items-center gap-2 text-[11px]">
          <Chip tone={urgTone}>
            {urgTone === "red" ? "⛔ " : urgTone === "amber" ? "▲ " : ""}
            {item.urgency}
          </Chip>
          {item.blocksProject ? (
            <span data-testid="human-task-blocks-project" title={cs.humanTasks.blocksProjectNote}>
              <Chip tone="red">{cs.humanTasks.blocksProjectTag}</Chip>
            </span>
          ) : null}
          <span style={{ color: "var(--text-tertiary)" }}>
            {item.projectName ?? "Factory"} · {relativeTime(item.createdAt)} ·{" "}
            {item.createdByLabel || "AI"}
          </span>
        </div>
        <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          ÚKOL #{item.id.slice(-5)}
        </span>
      </div>
      <h2
        className="af-display mb-5 mt-2 text-[26px] font-bold leading-tight"
        style={{ color: "var(--text-primary)" }}
      >
        {item.title}
      </h2>

      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.humanTasks.whatToDo}
          </div>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.context || "—"}
          </p>
        </Panel>
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.humanTasks.whyHuman}
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.whyHuman || "—"}
          </p>
        </Panel>
      </div>

      {item.expectedFinding ? (
        <Panel tone="green" className="mb-3 p-4">
          <div className="af-label mb-2" style={{ color: "var(--positive)" }}>
            {cs.humanTasks.expectedFinding}
          </div>
          <div className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
            {item.expectedFinding}
          </div>
        </Panel>
      ) : null}

      {item.status !== "OPEN" ? (
        <Panel tone="green" className="mb-3 p-4">
          <div className="af-label mb-1.5" style={{ color: "var(--positive)" }}>
            {cs.humanTasks.closedFinding}
          </div>
          <div className="whitespace-pre-wrap text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.finding ?? "—"}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {item.resolvedByLabel ?? "?"} · {item.resolvedAt ? relativeTime(item.resolvedAt) : ""}
          </div>
        </Panel>
      ) : (
        <Panel className="mb-3 p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.humanTasks.findingLabel}
          </div>
          <textarea
            className="w-full resize-y bg-transparent px-3 py-2 text-[12px] outline-none"
            style={{
              borderRadius: "var(--radius-box)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
              minHeight: 96,
            }}
            placeholder={cs.humanTasks.findingPlaceholder}
            maxLength={4000}
            value={finding}
            onChange={(e) => setFinding(e.target.value)}
            data-testid="human-task-finding"
          />
          <div className="mt-2 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            {cs.humanTasks.findingHint}
          </div>
        </Panel>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel tone="amber" className="p-3.5">
          <div className="af-label mb-1.5" style={{ color: "var(--warning)" }}>
            {cs.humanTasks.blocks}
          </div>
          <div className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.blockedTasks.length
              ? item.blockedTasks.map((t) => (
                  <Link
                    key={t.id}
                    to="/projects/$slug/tasks/$number"
                    params={{ slug: item.projectSlug ?? "", number: String(t.number) }}
                    className="block"
                  >
                    #{t.number} {t.title}
                  </Link>
                ))
              : "nic — informativní úkol"}
          </div>
          {item.blocksProject ? (
            <div className="mt-2 text-[12px] leading-relaxed" style={{ color: "var(--critical)" }}>
              {cs.humanTasks.blocksProjectNote}
            </div>
          ) : null}
        </Panel>
        {dismissOpen && item.status === "OPEN" ? (
          <Panel tone="red" className="p-3.5">
            <div className="af-label mb-1.5" style={{ color: "var(--critical)" }}>
              {cs.humanTasks.dismissLabel}
            </div>
            <textarea
              className="w-full resize-y bg-transparent px-3 py-2 text-[12px] outline-none"
              style={{
                borderRadius: "var(--radius-box)",
                border: "1px solid var(--border-default)",
                color: "var(--text-primary)",
                minHeight: 64,
              }}
              maxLength={1000}
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              data-testid="human-task-dismiss-reason"
            />
          </Panel>
        ) : null}
      </div>

      {error ? (
        <div className="mb-2 text-[12px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}

      {item.status === "OPEN" ? (
        <>
          <div className="flex flex-wrap gap-2.5">
            <Btn
              variant="primary"
              size="lg"
              disabled={completeMutation.isPending || !canClose || finding.trim().length < 2}
              onClick={() => {
                setError(null);
                setConfirming("DONE");
              }}
              data-testid="human-task-submit"
            >
              {cs.humanTasks.submit}
            </Btn>
            <Btn
              variant="danger"
              size="lg"
              disabled={dismissMutation.isPending || !canClose}
              onClick={() => {
                setError(null);
                if (!dismissOpen) {
                  setDismissOpen(true);
                  return;
                }
                if (dismissReason.trim().length >= 2) setConfirming("DISMISSED");
              }}
              data-testid="human-task-dismiss"
            >
              {cs.humanTasks.dismiss}
            </Btn>
            <Btn size="lg" onClick={() => openJarvis(cs.humanTasks.discussPrefill(item.title))}>
              {cs.humanTasks.discuss}
            </Btn>
          </div>
          <div className="mt-3 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {finding.trim().length < 2 ? cs.humanTasks.findingRequired : cs.humanTasks.afterNote}
          </div>
        </>
      ) : null}

      {confirming ? (
        <ConfirmModal
          testId="human-tasks"
          kicker={confirming === "DONE" ? cs.humanTasks.confirmTitle : cs.humanTasks.confirmDismissTitle}
          itemTitle={item.title}
          label={confirming === "DONE" ? cs.humanTasks.submit : cs.humanTasks.dismiss}
          body={confirming === "DONE" ? finding.trim() : dismissReason.trim()}
          bodyLabel={confirming === "DONE" ? cs.humanTasks.findingLabel : cs.humanTasks.dismissLabel}
          danger={confirming === "DISMISSED"}
          pending={completeMutation.isPending || dismissMutation.isPending}
          afterNote={cs.humanTasks.afterNote}
          onConfirm={() => {
            const which = confirming;
            setConfirming(null);
            if (which === "DONE") completeMutation.mutate();
            else dismissMutation.mutate();
          }}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}
